import { useState, useEffect, useCallback } from 'react';
import type { CohortOverviewStats } from '../../../types';
import { cohortsApi } from '../../../services/api/cohorts';

interface UseOverviewStatsOptions {
  cohortId: string;
  enabled?: boolean;
}

interface UseOverviewStatsResult {
  stats: CohortOverviewStats | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useOverviewStats({
  cohortId,
  enabled = true,
}: UseOverviewStatsOptions): UseOverviewStatsResult {
  const [stats, setStats] = useState<CohortOverviewStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch function that always works regardless of enabled state.
  // Used by refresh() so callers can force a refetch from any tab.
  const fetchStats = useCallback(async () => {
    if (!cohortId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await cohortsApi.getOverviewStats(cohortId);
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch overview stats:', err);
      setError('Failed to load overview statistics');
    } finally {
      setLoading(false);
    }
  }, [cohortId]);

  // Auto-fetch only when enabled (tab is active)
  useEffect(() => {
    if (enabled) {
      fetchStats();
    }
  }, [enabled, fetchStats]);

  return {
    stats,
    loading,
    error,
    refresh: fetchStats,
  };
}

export default useOverviewStats;
